import { inject, Injectable } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Router } from '@angular/router';
import { PermissionsService } from 'app/core/auth/permissions.service';
import { SupabaseService } from 'app/core/supabase/supabase.service';
import { Notification } from 'app/layout/common/notifications/notifications.types';

const UUID = '[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}';
/** Misma regla que el CHECK de la tabla: ruta del panel con query string simple, sin puntos ni barras invertidas. */
const INTERNAL_LINK = /^\/admin\/[A-Za-z0-9/_-]+(\?[A-Za-z0-9=&_-]*)?$/;
const PROPERTY_LINK = new RegExp(`^/admin/properties/(${UUID})/?$`, 'i');
const PROPERTY_NEGOTIATION_LINK = new RegExp(`^/admin/properties/(${UUID})/negotiation-conditions/?$`, 'i');
const CLOSING_LINK = new RegExp(`^/admin/closings/(${UUID})/?$`, 'i');

/**
 * Lleva al registro al que apunta una notificacion (propiedad, cierre, tramite...).
 *
 * Los enlaces los escriben triggers y servicios distintos, y varios quedaron
 * apuntando a rutas que no existen o que el rol no puede abrir: el clic no hacia
 * nada o devolvia al panel principal. Aqui se corrigen esos formatos y se elige
 * un destino que el usuario si pueda ver.
 */
@Injectable({ providedIn: 'root' })
export class NotificationNavigationService {
    private _router = inject(Router);
    private _permissions = inject(PermissionsService);
    private _supabase = inject(SupabaseService);
    private _snackBar = inject(MatSnackBar);

    async open(notification: Notification): Promise<void> {
        const link = notification.link?.trim();

        // Solo rutas internas del panel: un enlace externo nunca se sigue.
        if (!link || !INTERNAL_LINK.test(link)) {
            return;
        }

        const url = await this._resolve(link);
        // navigateByUrl y no routerLink: los enlaces pueden traer ?tab=..., que
        // routerLink codifica como parte de la ruta y deja de coincidir.
        const opened = url
            ? await this._router.navigateByUrl(url).catch(() => false)
            : false;

        if (!opened) {
            this._snackBar.open(
                'No se pudo abrir el detalle de esta notificación.',
                'Cerrar',
                { duration: 4000 }
            );
        }
    }

    private async _resolve(link: string): Promise<string | null> {
        // La ficha de la propiedad vive en /edit; el enlace sin sufijo no casa con ninguna ruta.
        const property = link.match(PROPERTY_LINK);
        if (property) {
            return `/admin/properties/${property[1]}/edit`;
        }

        const negotiation = link.match(PROPERTY_NEGOTIATION_LINK);
        if (negotiation) {
            return `/admin/properties/${negotiation[1]}/negotiation`;
        }

        // El asesor recibe avisos de sus cierres pero no puede ver cierres:
        // se le lleva a la propiedad de ese cierre.
        const closing = link.match(CLOSING_LINK);
        if (closing && !this._permissions.canView('closings.list')) {
            const propertyId = await this._closingPropertyId(closing[1]);
            return propertyId ? `/admin/properties/${propertyId}/edit` : null;
        }

        return link;
    }

    private async _closingPropertyId(closingId: string): Promise<string | null> {
        const { data } = await this._supabase.client
            .from('closings')
            .select('property_id')
            .eq('id', closingId)
            .maybeSingle();
        return (data as { property_id?: string } | null)?.property_id ?? null;
    }
}
